import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Alert,
  Box,
  Checkbox,
  FormGroup,
} from '@mui/material';
import { DialogTitleWithClose } from '../../common';
import { Warning as WarningIcon } from '@mui/icons-material';
import VersionStatusBadge from './VersionStatusBadge';
import { debugError } from '../../../utils/debugLog';

/**
 * Get allowed status transitions based on current status
 */
const getAllowedTransitions = currentStatus => {
  const transitions = {
    draft: ['active', 'deprecated', 'archived'],
    active: ['deprecated', 'archived'],
    deprecated: ['active', 'archived'],
    archived: [], // Cannot change from archived
  };
  return transitions[currentStatus?.toLowerCase()] || [];
};

/**
 * Get warning message for status transition
 */
const getTransitionWarning = (fromStatus, toStatus) => {
  if (fromStatus === 'active' && toStatus === 'deprecated') {
    return 'Deactivating active version will prevent new executions from using this version.';
  }
  if (toStatus === 'archived') {
    return 'Archiving a version cannot be undone. The version will be permanently archived.';
  }
  return null;
};

/**
 * VersionStatusDialog Component
 * Dialog for changing version status with transition validation
 */
const VersionStatusDialog = ({
  open,
  onClose,
  onConfirm,
  version,
  loading,
}) => {
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [reason, setReason] = useState('');
  const [createDraftCopy, setCreateDraftCopy] = useState(false);

  const currentStatus = version?.status || 'draft';
  const allowedTransitions = getAllowedTransitions(currentStatus);
  const warning = selectedStatus
    ? getTransitionWarning(currentStatus, selectedStatus)
    : null;

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSelectedStatus(null);
      setReason('');
      setCreateDraftCopy(false);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!selectedStatus) {
      return;
    }

    // Extract version number - handle both version_number (number) and version (string like "3" or "3.0.0")
    let versionNumber = version.version_number;
    if (!versionNumber && version.version) {
      // If version is a string, extract the number part
      const versionStr = String(version.version);
      const match = versionStr.match(/^(\d+)/);
      versionNumber = match ? parseInt(match[1], 10) : null;
    }

    if (!versionNumber) {
      debugError('Unable to extract version number from:', version);
      return;
    }

    const success = await onConfirm(versionNumber, {
      status: selectedStatus,
      reason: reason.trim() || null,
      create_draft_copy: createDraftCopy,
    });

    if (success) {
      onClose();
    }
  };

  const handleStatusChange = event => {
    setSelectedStatus(event.target.value);
  };

  if (!version) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithClose
        title="Change Version Status"
        onClose={onClose}
        disableClose={loading}
      />
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Current Status
          </Typography>
          <VersionStatusBadge status={currentStatus} />
        </Box>

        {allowedTransitions.length === 0 ? (
          <Alert severity="info">
            This version is archived and cannot be changed.
          </Alert>
        ) : (
          <>
            <FormControl component="fieldset" fullWidth sx={{ mb: 2 }}>
              <FormLabel component="legend">Select New Status</FormLabel>
              <RadioGroup
                value={selectedStatus || ''}
                onChange={handleStatusChange}
              >
                {allowedTransitions.map(status => (
                  <FormControlLabel
                    key={status}
                    value={status}
                    control={<Radio />}
                    label={
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <VersionStatusBadge status={status} />
                        <Typography variant="body2">
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>

            {warning && selectedStatus && (
              <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
                {warning}
              </Alert>
            )}

            {currentStatus === 'active' && selectedStatus === 'deprecated' && (
              <FormGroup sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={createDraftCopy}
                      onChange={e => setCreateDraftCopy(e.target.checked)}
                    />
                  }
                  label="Create new draft version from this version"
                />
              </FormGroup>
            )}

            <TextField
              fullWidth
              label="Reason (Optional)"
              placeholder="Enter reason for status change..."
              value={reason}
              onChange={e => setReason(e.target.value)}
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={
            loading || !selectedStatus || allowedTransitions.length === 0
          }
        >
          {loading ? <CircularProgress size={20} /> : 'Update Status'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VersionStatusDialog;
